import { useEffect, useRef } from 'react';

const TAU = Math.PI * 2;
const COLORS = [
  [255, 255, 255],
  [205, 210, 255],
  [170, 176, 255],
  [120, 230, 228],
  [232, 160, 255],
];
const WEIGHTS = [8, 1, 1, 1, 1]; // mostly white

/** Pre-render a tight, sharp glow sprite (concentrated core, fast falloff). */
function makeSprite([r, g, b]) {
  const SP = 32;
  const cv = document.createElement('canvas');
  cv.width = SP;
  cv.height = SP;
  const x = cv.getContext('2d');
  const grad = x.createRadialGradient(SP / 2, SP / 2, 0, SP / 2, SP / 2, SP / 2);
  grad.addColorStop(0, `rgba(${r},${g},${b},1)`);
  grad.addColorStop(0.16, `rgba(${r},${g},${b},0.78)`);
  grad.addColorStop(0.45, `rgba(${r},${g},${b},0.12)`);
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
  x.fillStyle = grad;
  x.fillRect(0, 0, SP, SP);
  return cv;
}

/**
 * Glamorous starfield — crisp white-cored stars with a tight colored halo,
 * depth parallax, a bright cursor constellation, and frequent shooting stars.
 * Sprite-based glow (no per-frame shadowBlur) + pauses when the tab is hidden.
 */
export default function Particles({ className = '', count = 120, shooting = true, linkDist = 260 }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5, px: -999, py: -999, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const sprites = COLORS.map(makeSprite);
    const pool = [];
    WEIGHTS.forEach((wt, i) => {
      for (let k = 0; k < wt; k++) pool.push(i);
    });

    let w, h, dpr, raf, running = true;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const n = reduce ? Math.min(count, 50) : count;
    const stars = Array.from({ length: n }, () => {
      const depth = 0.3 + Math.random() * 0.7;
      return {
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.6 + depth * (1.0 + Math.random() * 1.4),
        depth,
        vx: (Math.random() - 0.5) * 0.22 * depth,
        vy: (Math.random() - 0.5) * 0.22 * depth,
        tw: 0.6 + Math.random() * 2.2,
        phase: Math.random() * TAU,
        base: 0.5 + Math.random() * 0.5,
        si: pool[(Math.random() * pool.length) | 0],
      };
    });

    let shoots = [];
    let nextShoot = 30 + Math.random() * 60;
    const spawnShoot = () => {
      const fromLeft = Math.random() > 0.5;
      const speed = 7 + Math.random() * 6;
      shoots.push({
        x: fromLeft ? -60 : w + 60,
        y: Math.random() * h * 0.7,
        vx: (fromLeft ? 1 : -1) * speed,
        vy: 2.6 + Math.random() * 2.4,
        life: 1,
        len: 16 + Math.random() * 10,
        size: 1.6 + Math.random() * 1.4,
      });
    };

    const drawGlow = (sprite, px, py, diameter, alpha) => {
      ctx.globalAlpha = alpha;
      ctx.drawImage(sprite, px - diameter / 2, py - diameter / 2, diameter, diameter);
      ctx.globalAlpha = 1;
    };

    let t = 0;
    const render = () => {
      t += 1;
      ctx.clearRect(0, 0, w, h);
      const m = mouse.current;
      const ox = m.active ? (m.x - 0.5) * 26 : 0;
      const oy = m.active ? (m.y - 0.5) * 26 : 0;

      for (const s of stars) {
        if (!reduce) {
          s.x += s.vx;
          s.y += s.vy;
          if (s.x < -10) s.x = w + 10;
          if (s.x > w + 10) s.x = -10;
          if (s.y < -10) s.y = h + 10;
          if (s.y > h + 10) s.y = -10;
        }
        const twinkle = reduce ? 1 : 0.6 + 0.4 * Math.sin(t * 0.03 * s.tw + s.phase);
        const px = s.x + ox * s.depth;
        const py = s.y + oy * s.depth;
        const a = Math.min(1, s.base * twinkle + 0.1);
        // tight colored halo
        drawGlow(sprites[s.si], px, py, s.r * 4 * (0.85 + 0.3 * twinkle), a * 0.85);
        // sharp white core
        ctx.globalAlpha = a;
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(px, py, Math.max(0.5, s.r * 0.7), 0, TAU);
        ctx.fill();
        ctx.globalAlpha = 1;
      }

      // Constellation burst around the cursor
      if (m.px > -500) {
        const halo = ctx.createRadialGradient(m.px, m.py, 0, m.px, m.py, 80);
        halo.addColorStop(0, 'rgba(109,94,252,0.18)');
        halo.addColorStop(1, 'rgba(109,94,252,0)');
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(m.px, m.py, 80, 0, TAU);
        ctx.fill();

        for (const s of stars) {
          const sx = s.x + ox * s.depth;
          const sy = s.y + oy * s.depth;
          const d = Math.hypot(sx - m.px, sy - m.py);
          if (d < linkDist) {
            const k = 1 - d / linkDist;
            ctx.strokeStyle = `rgba(150,160,255,${0.55 * k})`;
            ctx.lineWidth = 0.6 + 1.8 * k;
            ctx.beginPath();
            ctx.moveTo(sx, sy);
            ctx.lineTo(m.px, m.py);
            ctx.stroke();
            drawGlow(sprites[s.si], sx, sy, s.r * 7 * (1 + k), 0.6 * k);
            // brighten the linked star's core
            ctx.globalAlpha = 0.6 * k + 0.4;
            ctx.fillStyle = '#ffffff';
            ctx.beginPath();
            ctx.arc(sx, sy, Math.max(0.8, s.r * (0.9 + k)), 0, TAU);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        }
      }

      // Shooting stars — frequent + chunky, with a sharp head
      if (shooting && !reduce) {
        if (shoots.length < 3 && t > nextShoot) {
          spawnShoot();
          nextShoot = t + 35 + Math.random() * 70;
        }
        for (const sh of shoots) {
          sh.x += sh.vx;
          sh.y += sh.vy;
          sh.life -= 0.01;
          const tailX = sh.x - sh.vx * sh.len;
          const tailY = sh.y - sh.vy * sh.len;
          const a = Math.max(0, sh.life);
          const grad = ctx.createLinearGradient(sh.x, sh.y, tailX, tailY);
          grad.addColorStop(0, `rgba(255,255,255,${a})`);
          grad.addColorStop(0.4, `rgba(190,200,255,${a * 0.5})`);
          grad.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.strokeStyle = grad;
          ctx.lineWidth = 2.4;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(sh.x, sh.y);
          ctx.lineTo(tailX, tailY);
          ctx.stroke();
          drawGlow(sprites[0], sh.x, sh.y, sh.size * 6, a);
          ctx.globalAlpha = a;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.arc(sh.x, sh.y, sh.size, 0, TAU);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
        shoots = shoots.filter((sh) => sh.life > 0 && sh.x > -120 && sh.x < w + 120 && sh.y < h + 120);
      }

      raf = requestAnimationFrame(render);
    };
    render();

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        raf = requestAnimationFrame(render);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current = {
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
        px: e.clientX - rect.left,
        py: e.clientY - rect.top,
        active: true,
      };
    };
    const onLeave = () => {
      mouse.current.active = false;
      mouse.current.px = -999;
      mouse.current.py = -999;
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [count, shooting, linkDist]);

  return (
    <canvas ref={canvasRef} className={`pointer-events-none absolute inset-0 h-full w-full ${className}`} aria-hidden />
  );
}
